import { FC, ReactNode } from 'react';
import { Button, makeStyles } from '@material-ui/core';

export type ActionButtonVariant = 'danger' | 'outline' | 'primary';

interface ActionButtonProps {
  variant?: ActionButtonVariant;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'data-testid'?: string;
  className?: string;
}

const useStyles = makeStyles(theme => ({
  primary: {
    backgroundColor: '#0066CC',
    color: '#ffffff',
    boxShadow: 'none',
    borderRadius: '20px',
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 600,
    padding: '8px 20px',
    whiteSpace: 'nowrap',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:hover': {
      backgroundColor: '#0052A3',
      color: '#ffffff',
      boxShadow: 'none',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:disabled': {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.grey[600],
      border: 'none',
    },
  },
  danger: {
    backgroundColor: theme.palette.error.main,
    color: '#ffffff',
    boxShadow: 'none',
    borderRadius: '20px',
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 600,
    padding: '8px 20px',
    whiteSpace: 'nowrap',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
      color: '#ffffff',
      boxShadow: 'none',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:disabled': {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.grey[600],
      border: 'none',
    },
  },
  outline: {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    boxShadow: 'none',
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: '20px',
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 600,
    padding: '8px 20px',
    whiteSpace: 'nowrap',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
      border: `2px solid ${theme.palette.text.secondary}`,
      boxShadow: 'none',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:disabled': {
      backgroundColor: 'transparent',
      color: theme.palette.grey[600],
      border: `2px solid ${theme.palette.grey[300]}`,
    },
  },
}));

export const ActionButton: FC<ActionButtonProps> = ({
  variant = 'primary',
  onClick,
  disabled = false,
  children,
  'data-testid': dataTestId,
  className,
}) => {
  const classes = useStyles();

  return (
    <Button
      className={`${classes[variant]} ${className || ''}`.trim()}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      variant="contained"
    >
      {children}
    </Button>
  );
};
